import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, MenuItem, Stack, CircularProgress, FormControlLabel, Checkbox, Autocomplete, Avatar, Typography, IconButton, Paper } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { fetchAnnouncementById, updateAnnouncement as updateAnnouncementAction } from '../controllers/announcementsController';
import { searchClubEvents } from '../controllers/clubEventsController';
import { searchGroups } from '../controllers/groupManagementController';
import { searchMembers, fetchAllMembers, fetchMemberById } from '../controllers/memberManagementController';
import { resolveGroupsAssetUrl } from '../api/httpClientGroups';
import RichTextEditor from '../components/RichTextEditor';

const EditAnnouncementDialog = ({ open, onClose, announcementId, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState(null);

  const [eventQuery, setEventQuery] = useState('');
  const [eventOptions, setEventOptions] = useState([]);
  const [eventLoading, setEventLoading] = useState(false);

  const [groupQuery, setGroupQuery] = useState('');
  const [groupOptions, setGroupOptions] = useState([]);
  const [groupLoading, setGroupLoading] = useState(false);

  const [memberQuery, setMemberQuery] = useState('');
  const [memberOptions, setMemberOptions] = useState([]);
  const [memberLoading, setMemberLoading] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState([]);

  const [newMemberQuery, setNewMemberQuery] = useState('');
  const [newMemberOptions, setNewMemberOptions] = useState([]);
  const [newMemberLoading, setNewMemberLoading] = useState(false);

  useEffect(() => {
    if (!open || !announcementId) return;
    let active = true;
    setLoading(true);
    setError('');
    fetchAnnouncementById(announcementId)
      .then((data) => {
        if (!active) return;
        setForm({
          id: data.id,
          title: data.title || '',
          content: data.content || '',
          category: data.category ?? 0,
          relatedEntityId: data.relatedEntityId || null,
          relatedEntity: null,
          sendChannels: (() => { const arr=[]; if ((data.sendChannels & 1)===1) arr.push(1); if ((data.sendChannels & 2)===2) arr.push(2); if ((data.sendChannels & 4)===4) arr.push(4); if ((data.sendChannels & 8)===8) arr.push(8); return arr; })(),
          targetType: data.targetType ?? 0,
          targetData: Array.isArray(data.targetData) ? data.targetData : [],
          visibility: data.visibility ?? 0,
          automationType: data.automationType ?? 0,
          automationRule: data.automationRule || '',
          publishAt: data.publishAt || null,
          expireAt: data.expireAt || null,
          scheduledOnceAt: '',
          recurrenceType: 'weekly',
          recurrenceDayOfWeek: 'MON',
          recurrenceDayOfMonth: 1,
          recurrenceMonth: 1,
        });
        if (data.targetType === 2) {
          setSelectedMembers((data.targetData || []).map((id) => ({ id, fullName: id })));
        } else {
          setSelectedMembers([]);
        }
      })
      .catch((e) => { if (active) setError(e?.message || 'Duyuru alınamadı'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open, announcementId]);

  useEffect(() => {
    if (!open) return;
    if (Number(form?.category) !== 1) return;
    if (!eventQuery || eventQuery.length < 2) { setEventOptions([]); return; }
    let active = true;
    setEventLoading(true);
    searchClubEvents({ keyword: eventQuery, pageNumber: 1, pageSize: 10 })
      .then(({ data }) => { if (active) setEventOptions(Array.isArray(data) ? data : []); })
      .catch(() => { if (active) setEventOptions([]); })
      .finally(() => { if (active) setEventLoading(false); });
    return () => { active = false; };
  }, [open, form?.category, eventQuery]);

  useEffect(() => {
    if (!open || !form) return;
    if (Number(form.category) !== 6) {
      setNewMemberOptions([]);
      return;
    }
    let active = true;
    setNewMemberLoading(true);
    const loadMembers = newMemberQuery?.trim()
      ? searchMembers(newMemberQuery.trim()).then((data) => (Array.isArray(data) ? data : []))
      : fetchAllMembers({ pageNumber: 1, pageSize: 50 }).then(({ data }) => (Array.isArray(data) ? data : []));
    loadMembers
      .then((data) => { if (active) setNewMemberOptions(data); })
      .catch(() => { if (active) setNewMemberOptions([]); })
      .finally(() => { if (active) setNewMemberLoading(false); });
    return () => { active = false; };
  }, [open, form?.category, newMemberQuery]);

  useEffect(() => {
    if (!open || !form) return;
    if (Number(form.category) !== 6 || !form.relatedEntityId) return;
    const currentId = form.relatedEntityId;
    const hasMatchingEntity = form.relatedEntity && (form.relatedEntity.id === currentId || form.relatedEntity.Id === currentId);
    if (hasMatchingEntity) return;
    let active = true;
    fetchMemberById(currentId)
      .then((member) => {
        if (active && member) setForm((f) => ({ ...f, relatedEntity: member }));
      })
      .catch(() => {});
    return () => { active = false; };
  }, [open, form?.category, form?.relatedEntityId]);

  useEffect(() => {
    if (!open) return;
    if (Number(form?.targetType) !== 1) return;
    if (!groupQuery || groupQuery.length < 2) { setGroupOptions([]); return; }
    let active = true;
    setGroupLoading(true);
    searchGroups({ keyword: groupQuery })
      .then((data) => { if (active) setGroupOptions(Array.isArray(data) ? data : []); })
      .catch(() => { if (active) setGroupOptions([]); })
      .finally(() => { if (active) setGroupLoading(false); });
    return () => { active = false; };
  }, [open, form?.targetType, groupQuery]);

  useEffect(() => {
    if (!open) return;
    if (Number(form?.targetType) !== 2) return;
    if (!memberQuery || memberQuery.length < 2) { setMemberOptions([]); return; }
    let active = true;
    setMemberLoading(true);
    searchMembers(memberQuery)
      .then((data) => { if (active) setMemberOptions(Array.isArray(data) ? data : []); })
      .catch(() => { if (active) setMemberOptions([]); })
      .finally(() => { if (active) setMemberLoading(false); });
    return () => { active = false; };
  }, [open, form?.targetType, memberQuery]);

  useEffect(() => {
    if (!open || !form) return;
    if (Number(form.targetType) === 1 || Number(form.targetType) === 2) {
      if (form.visibility !== 0) setForm((f) => ({ ...f, visibility: 0 }));
    }
  }, [open, form?.targetType]);

  const toIntMask = (arr) => (arr || []).reduce((acc, v) => acc | Number(v), 0);

  const handleSave = async () => {
    if (!form) return;
    setSaving(true);
    setError('');
    try {
      const payload = {
        id: form.id,
        title: form.title,
        content: form.content,
        category: Number(form.category) || 0,
        relatedEntityId: (Number(form.category) === 1 || Number(form.category) === 6) ? (form.relatedEntityId || null) : null,
        sendChannels: toIntMask(form.sendChannels),
        targetType: Number(form.targetType) || 0,
        targetData: form.targetType === 0 ? null : (
          form.targetType === 1
            ? (form.targetData && form.targetData.length ? form.targetData : (form.relatedEntityId ? [String(form.relatedEntityId)] : []))
            : (Array.isArray(form.targetData) ? form.targetData : [])
        ),
        visibility: (Number(form.targetType) === 1 || Number(form.targetType) === 2) ? 0 : (Number(form.visibility) || 0),
        automationType: Number(form.automationType) || 0,
        automationRule: (() => {
          if (Number(form.automationType) === 1) {
            return form.scheduledOnceAt ? new Date(`${form.scheduledOnceAt}:00`).toISOString() : form.automationRule || null;
          }
          if (Number(form.automationType) === 2) {
            if (form.recurrenceType === 'weekly') return `WEEKLY:${form.recurrenceDayOfWeek}`;
            if (form.recurrenceType === 'monthly') return `MONTHLY:${Math.max(1, Math.min(31, Number(form.recurrenceDayOfMonth) || 1))}`;
            if (form.recurrenceType === 'yearly') {
              const m = Math.max(1, Math.min(12, Number(form.recurrenceMonth) || 1));
              const d = Math.max(1, Math.min(31, Number(form.recurrenceDayOfMonth) || 1));
              const pad = (n) => String(n).padStart(2, '0');
              return `YEARLY:${pad(m)}-${pad(d)}`;
            }
            return form.automationRule || null;
          }
          return null;
        })(),
        isPublished: Boolean(form.isPublished),
        publishAt: form.publishAt || null,
        expireAt: form.expireAt ? new Date(form.expireAt).toISOString() : null,
      };
      await updateAnnouncementAction(payload);
      onSaved?.();
    } catch (e) {
      setError(e?.message || 'Duyuru güncellenemedi');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle
        sx={{
          fontSize: '18px',
          fontWeight: 500,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pr: 3,
        }}
      >
        Duyuruyu Düzenle
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ py: 2, px: 3 }}>
        {loading || !form ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
        ) : (
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                Başlık
              </Typography>
              <TextField
                fullWidth
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder=""
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '5px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0E4D94',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: 1.2,
                    px: 1.5,
                    fontSize: '14px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e6e6e6',
                  },
                }}
              />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>İçerik</Typography>
              <RichTextEditor
                value={form.content}
                onChange={(html) => setForm((f) => ({ ...f, content: html }))}
                placeholder="Duyuru içeriğini buraya yazın..."
              />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                Kategori
              </Typography>
              <TextField
                fullWidth
                select
                value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: Number(e.target.value), relatedEntityId: null, relatedEntity: null }))}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '5px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0E4D94',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: 1.2,
                    px: 1.5,
                    fontSize: '14px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e6e6e6',
                  },
                }}
              >
              <MenuItem value={0}>Genel</MenuItem>
              <MenuItem value={1}>Etkinlik</MenuItem>
              <MenuItem value={2}>Proje</MenuItem>
              <MenuItem value={3}>Vefat</MenuItem>
              <MenuItem value={4}>Evlilik</MenuItem>
              <MenuItem value={5}>Doğum</MenuItem>
              <MenuItem value={6}>Yeni Üye Katılımı</MenuItem>
              <MenuItem value={7}>Bayram Tebrikleri</MenuItem>
            </TextField>
            </Box>
            {Number(form.category) === 6 && (
              <Box>
                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                  Üye
                </Typography>
              <Autocomplete
                options={newMemberOptions}
                loading={newMemberLoading}
                value={form.relatedEntity || null}
                onChange={(e, v) => setForm((f) => ({ ...f, relatedEntityId: v?.id || v?.Id || null, relatedEntity: v || null }))}
                onInputChange={(e, v) => setNewMemberQuery(v)}
                filterOptions={(x) => x}
                isOptionEqualToValue={(o, v) => (o?.id || o?.Id) === (v?.id || v?.Id)}
                getOptionLabel={(o) => o?.fullName || o?.FullName || ''}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option.id || option.Id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar src={resolveGroupsAssetUrl(option.profilePictureUrl || option.ProfilePictureUrl || option.profilePicture || option.ProfilePicture)} sx={{ width: 24, height: 24 }} />
                    {option.fullName || option.FullName}
                  </Box>
                )}
                renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Üye ara veya seç..."
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '5px',
                          py: 0,
                          px: 0,
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#0E4D94',
                          },
                        },
                        '& .MuiAutocomplete-inputRoot': {
                          py: '2.5px',
                          px: '12px',
                        },
                        '& .MuiAutocomplete-input': {
                          py: 0,
                          px: 0,
                          fontSize: '14px',
                        },
                        '& .MuiInputBase-input': {
                          py: 1.2,
                          px: 1.5,
                          fontSize: '14px',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#e6e6e6',
                        },
                      }}
                      InputProps={{ ...params.InputProps, endAdornment: (<>{newMemberLoading ? <CircularProgress color="inherit" size={16} /> : null}{params.InputProps.endAdornment}</>) }}
                    />
                )}
              />
              </Box>
            )}
            {Number(form.category) === 6 && form.relatedEntity && (
              <Paper variant="outlined" sx={{ p: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Avatar src={resolveGroupsAssetUrl(form.relatedEntity.profilePictureUrl || form.relatedEntity.ProfilePictureUrl || form.relatedEntity.profilePicture || form.relatedEntity.ProfilePicture)} sx={{ width: 32, height: 32 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {form.relatedEntity.fullName || form.relatedEntity.FullName}
                  </Typography>
                </Stack>
                <Button size="small" onClick={() => setForm((f) => ({ ...f, relatedEntityId: null, relatedEntity: null }))}>
                  Kaldır
                </Button>
              </Paper>
            )}
            {Number(form.category) === 1 && (
              <Box>
                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                  Etkinlik
                </Typography>
              <Autocomplete
                options={eventOptions}
                loading={eventLoading}
                value={form.relatedEntity || null}
                onChange={(e, v) => setForm((f) => ({ ...f, relatedEntityId: v?.id || null, relatedEntity: v || null }))}
                onInputChange={(e, v) => setEventQuery(v)}
                filterOptions={(x) => x}
                isOptionEqualToValue={(o, v) => (o?.id || o?.Id) === (v?.id || v?.Id)}
                getOptionLabel={(o) => o?.title ? `${o.title} ${o.city ? `- ${o.city}` : ''}` : ''}
                renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder=""
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '5px',
                          py: 0,
                          px: 0,
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#0E4D94',
                          },
                        },
                        '& .MuiAutocomplete-inputRoot': {
                          py: '2.5px',
                          px: '12px',
                        },
                        '& .MuiAutocomplete-input': {
                          py: 0,
                          px: 0,
                          fontSize: '14px',
                        },
                        '& .MuiInputBase-input': {
                          py: 1.2,
                          px: 1.5,
                          fontSize: '14px',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#e6e6e6',
                        },
                      }}
                      InputProps={{ ...params.InputProps, endAdornment: (<>{eventLoading ? <CircularProgress color="inherit" size={16} /> : null}{params.InputProps.endAdornment}</>) }}
                    />
                )}
              />
              </Box>
            )}
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                Hedef Tipi
              </Typography>
              <TextField
                fullWidth
                select
                value={form.targetType}
                onChange={(e) => { const t = Number(e.target.value); setForm((f) => ({ ...f, targetType: t, targetData: t===2? (Array.isArray(f.targetData)?f.targetData:[]) : (t===1? (f.targetData||[]): [] ) })); setSelectedMembers([]); }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '5px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0E4D94',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: 1.2,
                    px: 1.5,
                    fontSize: '14px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e6e6e6',
                  },
                }}
              >
              <MenuItem value={0}>Tüm Üyeler</MenuItem>
              <MenuItem value={1}>Belirli Gruplar</MenuItem>
              <MenuItem value={2}>Belirli Üyeler</MenuItem>
            </TextField>
            </Box>
            {Number(form.targetType) === 1 ? (
              <Box>
                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                  Grup
                </Typography>
              <Autocomplete
                options={groupOptions}
                loading={groupLoading}
                onChange={(e, v) => setForm((f) => ({ ...f, targetData: v?.id ? [String(v.id)] : [] }))}
                onInputChange={(e, v) => setGroupQuery(v)}
                filterOptions={(x) => x}
                getOptionLabel={(o) => o?.name ? `${o.name} ${typeof o.memberCount === 'number' ? `(${o.memberCount})` : ''}` : ''}
                renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder=""
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '5px',
                          py: 0,
                          px: 0,
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#0E4D94',
                          },
                        },
                        '& .MuiAutocomplete-inputRoot': {
                          py: '2.5px',
                          px: '12px',
                        },
                        '& .MuiAutocomplete-input': {
                          py: 0,
                          px: 0,
                          fontSize: '14px',
                        },
                        '& .MuiInputBase-input': {
                          py: 1.2,
                          px: 1.5,
                          fontSize: '14px',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#e6e6e6',
                        },
                      }}
                      InputProps={{ ...params.InputProps, endAdornment: (<>{groupLoading ? <CircularProgress color="inherit" size={16} /> : null}{params.InputProps.endAdornment}</>) }}
                    />
                )}
              />
              </Box>
            ) : Number(form.targetType) === 2 ? (
              <Box>
                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                  Üye
                </Typography>
              <Autocomplete
                multiple
                options={memberOptions}
                loading={memberLoading}
                value={Array.isArray(selectedMembers) ? selectedMembers : []}
                onChange={(e, v) => { setSelectedMembers(v || []); setForm((f) => ({ ...f, targetData: (v || []).map((m) => m.id || m.Id) })); }}
                onInputChange={(e, v) => setMemberQuery(v)}
                filterOptions={(x) => x}
                isOptionEqualToValue={(o, v) => (o?.id || o?.Id) === (v?.id || v?.Id)}
                getOptionLabel={(o) => o?.fullName || o?.FullName || ''}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option.id || option.Id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar src={resolveGroupsAssetUrl(option.profilePictureUrl || option.ProfilePictureUrl || option.profilePicture || option.ProfilePicture)} sx={{ width: 24, height: 24 }} />
                    {option.fullName || option.FullName}
                  </Box>
                )}
                renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder=""
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '5px',
                          py: 0,
                          px: 0,
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#0E4D94',
                          },
                        },
                        '& .MuiAutocomplete-inputRoot': {
                          py: '2.5px',
                          px: '12px',
                        },
                        '& .MuiAutocomplete-input': {
                          py: 0,
                          px: 0,
                          fontSize: '14px',
                        },
                        '& .MuiInputBase-input': {
                          py: 1.2,
                          px: 1.5,
                          fontSize: '14px',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#e6e6e6',
                        },
                      }}
                      InputProps={{ ...params.InputProps, endAdornment: (<>{memberLoading ? <CircularProgress color="inherit" size={16} /> : null}{params.InputProps.endAdornment}</>) }}
                    />
                )}
              />
              </Box>
            ) : null}
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                Görünürlük
              </Typography>
              <TextField
                fullWidth
                select
                value={form.visibility}
                onChange={(e) => setForm((f) => ({ ...f, visibility: Number(e.target.value) }))}
                disabled={Number(form.targetType) === 1 || Number(form.targetType) === 2}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '5px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0E4D94',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: 1.2,
                    px: 1.5,
                    fontSize: '14px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e6e6e6',
                  },
                }}
              >
              <MenuItem value={0}>Sadece Üyeler</MenuItem>
              <MenuItem value={1}>Herkese Açık</MenuItem>
            </TextField>
            </Box>
            <Stack>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Yayın Kanalları</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }}>
                {[{ label: 'E-posta', val: 1 }, { label: 'SMS', val: 2 }, { label: 'Mobil Uygulama Bildirimi', val: 4 }, { label: 'Mobil Uygulama', val: 8 }].map((opt) => (
                  <FormControlLabel key={opt.val} control={<Checkbox checked={form.sendChannels.includes(opt.val)} onChange={(e) => {
                    setForm((f) => {
                      const sel = new Set(f.sendChannels);
                      if (e.target.checked) sel.add(opt.val); else sel.delete(opt.val);
                      return { ...f, sendChannels: Array.from(sel) };
                    });
                  }} />} label={opt.label} />
                ))}
              </Stack>
            </Stack>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                Otomasyon
              </Typography>
              <TextField
                fullWidth
                select
                value={form.automationType}
                onChange={(e) => setForm((f) => ({ ...f, automationType: Number(e.target.value) }))}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '5px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0E4D94',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: 1.2,
                    px: 1.5,
                    fontSize: '14px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e6e6e6',
                  },
                }}
              >
              <MenuItem value={0}>Manuel</MenuItem>
              <MenuItem value={1}>Tek Sefer (Zamanlı)</MenuItem>
              <MenuItem value={2}>Tekrarlayan</MenuItem>
            </TextField>
            </Box>
            {Number(form.automationType) === 1 && (
              <Box>
                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                  Yayınlanacak Tarih
                </Typography>
                <TextField
                  type="datetime-local"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={form.scheduledOnceAt || ''}
                  onChange={(e) => setForm((f) => ({ ...f, scheduledOnceAt: e.target.value }))}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '5px',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#0E4D94',
                      },
                    },
                    '& .MuiInputBase-input': {
                      py: 1.2,
                      px: 1.5,
                      fontSize: '14px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e6e6e6',
                    },
                  }}
                />
              </Box>
            )}
            {Number(form.automationType) === 2 && (
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                    Tekrar
                  </Typography>
                  <TextField
                    fullWidth
                    select
                    value={form.recurrenceType}
                    onChange={(e) => setForm((f) => ({ ...f, recurrenceType: e.target.value }))}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '5px',
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#0E4D94',
                        },
                      },
                      '& .MuiInputBase-input': {
                        py: 1.2,
                        px: 1.5,
                        fontSize: '14px',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#e6e6e6',
                      },
                    }}
                  >
                  <MenuItem value="weekly">Haftada bir</MenuItem>
                  <MenuItem value="monthly">Ayda bir</MenuItem>
                  <MenuItem value="yearly">Yılda bir</MenuItem>
                </TextField>
                </Box>
                {form.recurrenceType === 'weekly' && (
                  <Box>
                    <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                      Gün
                    </Typography>
                    <TextField
                      fullWidth
                      select
                      value={form.recurrenceDayOfWeek}
                      onChange={(e) => setForm((f) => ({ ...f, recurrenceDayOfWeek: e.target.value }))}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '5px',
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#0E4D94',
                          },
                        },
                        '& .MuiInputBase-input': {
                          py: 1.2,
                          px: 1.5,
                          fontSize: '14px',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#e6e6e6',
                        },
                      }}
                    >
                    <MenuItem value="MON">Pazartesi</MenuItem>
                    <MenuItem value="TUE">Salı</MenuItem>
                    <MenuItem value="WED">Çarşamba</MenuItem>
                    <MenuItem value="THU">Perşembe</MenuItem>
                    <MenuItem value="FRI">Cuma</MenuItem>
                    <MenuItem value="SAT">Cumartesi</MenuItem>
                    <MenuItem value="SUN">Pazar</MenuItem>
                  </TextField>
                  </Box>
                )}
                {form.recurrenceType === 'monthly' && (
                  <Box>
                    <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                      Ayın Kaçıncı Günü
                    </Typography>
                    <TextField
                      fullWidth
                      type="number"
                      inputProps={{ min: 1, max: 31 }}
                      value={form.recurrenceDayOfMonth}
                      onChange={(e) => setForm((f) => ({ ...f, recurrenceDayOfMonth: Math.max(1, Math.min(31, Number(e.target.value) || 1)) }))}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '5px',
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#0E4D94',
                          },
                        },
                        '& .MuiInputBase-input': {
                          py: 1.2,
                          px: 1.5,
                          fontSize: '14px',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#e6e6e6',
                        },
                      }}
                    />
                  </Box>
                )}
                {form.recurrenceType === 'yearly' && (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                        Ay
                      </Typography>
                      <TextField
                        fullWidth
                        select
                        value={form.recurrenceMonth}
                        onChange={(e) => setForm((f) => ({ ...f, recurrenceMonth: Number(e.target.value) }))}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '5px',
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#0E4D94',
                            },
                          },
                          '& .MuiInputBase-input': {
                            py: 1.2,
                            px: 1.5,
                            fontSize: '14px',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#e6e6e6',
                          },
                        }}
                      >
                        <MenuItem value={1}>Ocak</MenuItem>
                        <MenuItem value={2}>Şubat</MenuItem>
                        <MenuItem value={3}>Mart</MenuItem>
                        <MenuItem value={4}>Nisan</MenuItem>
                        <MenuItem value={5}>Mayıs</MenuItem>
                        <MenuItem value={6}>Haziran</MenuItem>
                        <MenuItem value={7}>Temmuz</MenuItem>
                        <MenuItem value={8}>Ağustos</MenuItem>
                        <MenuItem value={9}>Eylül</MenuItem>
                        <MenuItem value={10}>Ekim</MenuItem>
                        <MenuItem value={11}>Kasım</MenuItem>
                        <MenuItem value={12}>Aralık</MenuItem>
                    </TextField>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                        Gün
                      </Typography>
                      <TextField
                        fullWidth
                        type="number"
                        inputProps={{ min: 1, max: 31 }}
                        value={form.recurrenceDayOfMonth}
                        onChange={(e) => setForm((f) => ({ ...f, recurrenceDayOfMonth: Math.max(1, Math.min(31, Number(e.target.value) || 1)) }))}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '5px',
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#0E4D94',
                            },
                          },
                          '& .MuiInputBase-input': {
                            py: 1.2,
                            px: 1.5,
                            fontSize: '14px',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#e6e6e6',
                          },
                        }}
                      />
                    </Box>
                  </Stack>
                )}
              </Stack>
            )}
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                Yayın Bitiş Tarihi
              </Typography>
              <TextField
                type="datetime-local"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.expireAt ? String(form.expireAt).slice(0,16) : ''}
                onChange={(e) => setForm((f) => ({ ...f, expireAt: e.target.value ? `${e.target.value}:00` : '' }))}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '5px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0E4D94',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: 1.2,
                    px: 1.5,
                    fontSize: '14px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e6e6e6',
                  },
                }}
              />
            </Box>
            {error && <Box sx={{ color: 'error.main', fontSize: 13 }}>{error}</Box>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ py: 2, px: 3 }}>
        <Button
          onClick={onClose}
          disabled={saving}
          sx={{
            textTransform: 'none',
            color: '#0E4D94',
          }}
        >
          İptal
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !form?.title || !form?.content || (Number(form?.automationType) === 1 && !form?.scheduledOnceAt)}
          sx={{
            borderRadius: '5px',
            textTransform: 'none',
            fontSize: '14px',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
            },
          }}
        >
          {saving ? 'Kaydediliyor...' : 'Kaydet'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditAnnouncementDialog;







